import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/ui/ToastContext";
import { Loader2, DollarSign, Calendar, Clock, Building2, User, ChevronLeft, ChevronRight } from "lucide-react";
import UiText from '../components/UiText';
import {
  DndContext,
  pointerWithin,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0][0].toUpperCase();
};

const AVATAR_COLORS = [
  "#3b82f6", "#8b5cf6", "#ec4899", "#10b981", "#f59e0b", "#ef4444", "#06b6d4",
];

const avatarColor = (name) => {
  if (!name) return "#94a3b8";
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// Draggable Card Component
const DealCard = ({ deal, stage }) => {
  const navigate = useNavigate();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: deal.id,
    data: { type: 'deal', deal },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const collabs = deal.collab_names || [];

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`rounded-xl mt-2 cursor-grab transition-all overflow-hidden group ${
        isDragging ? "shadow-2xl scale-105 opacity-100" : "shadow-md hover:shadow-xl"
      }`}
      style={{
        ...style,
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)",
        border: "1px solid #e2e8f0",
      }}
    >
      {/* Top accent bar */}
      <div
        className="h-1 w-full"
        style={{ backgroundColor: stage.color || '#3b82f6' }}
      ></div>

      <div className="p-4">
        {/* Deal Name */}
        <UiText
          variant="section"
          as="h3"
          className="font-bold text-gray-900 mb-3 line-clamp-2 text-sm leading-tight hover:text-blue-600 cursor-pointer transition-colors"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => navigate(`/deal-details/${deal.id}`)}
        >
          {deal.title}
        </UiText>

        {/* Value */}
        <div className="mb-3 p-2.5 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100">
          <div className="flex items-center gap-2">
            <DollarSign size={14} className="text-emerald-600 flex-shrink-0" />
            <span className="text-sm font-bold text-emerald-700">
              ${parseFloat(deal.amount || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </span>
          </div>
        </div>

        {/* Info grid */}
        <div className="space-y-2 mb-3">
          {/* Close Date */}
          <div className="flex items-center gap-2 text-xs">
            <Calendar size={13} className="text-blue-500 flex-shrink-0" />
            <span className="text-gray-600">
              {deal.close_date ? new Date(deal.close_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : "No close date"}
            </span>
          </div>

          {/* Last Updated */}
          <div className="flex items-center gap-2 text-xs">
            <Clock size={13} className="text-amber-500 flex-shrink-0" />
            <span className="text-gray-600">
              Updated {deal.updated_at ? new Date(deal.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : "recently"}
            </span>
          </div>

          {/* Owner */}
          {deal.created_by_name && (
            <div className="flex items-center gap-2 text-xs">
              <User size={13} className="text-indigo-400 flex-shrink-0" />
              <span className="text-gray-600 truncate">{deal.created_by_name}</span>
            </div>
          )}
        </div>

        {/* Company footer */}
        <div className="pt-2.5 border-t border-gray-100" style={{ minHeight: '52px' }}>
          <div className="flex items-center gap-1.5 mb-2">
            <Building2 size={12} className="text-gray-400 flex-shrink-0" />
            <UiText
              variant="label"
              className="text-gray-500 text-xs truncate"
            >
              {deal.company_name || "Unknown company"}
            </UiText>
          </div>

          {/* Collaborators */}
          <div className="flex items-center gap-1 flex-wrap" style={{ minHeight: '22px' }}>
            {collabs.length > 0 ? (
              <>
                {collabs.slice(0, 4).map((name, i) => (
                <div
                  key={i}
                  title={name}
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: avatarColor(name),
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1.5px solid #fff",
                    boxShadow: "0 0 0 1px #e2e8f0",
                  }}
                >
                  {getInitials(name)}
                </div>
              ))}
              {collabs.length > 4 && (
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "#94a3b8",
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1.5px solid #fff",
                    boxShadow: "0 0 0 1px #e2e8f0",
                  }}
                >
                  +{collabs.length - 4}
                </div>
              )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

// Droppable Column Component
const StageColumn = ({ stage, deals }) => {
  const { setNodeRef } = useSortable({
    id: stage.key,
    data: { type: 'stage' },
    disabled: true,
  });

  const totalValue = deals.reduce((sum, deal) => sum + parseFloat(deal.amount || 0), 0);

  return (
    <div
      ref={setNodeRef}
      className="bg-white rounded-xl shadow-sm border border-gray-200 transition-all flex flex-col"
      style={{ flex: "0 0 260px", width: "260px", height: "calc(100vh - 160px)" }}
    >
      {/* Column Header - stays fixed */}
      <div className="p-3 pb-3 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: stage.color || '#94a3b8' }}
            ></span>
            <UiText variant="section" as="h2" className="text-gray-800">{stage.label}</UiText>
          </div>
        </div>
        <div className="flex items-center justify-between" style={{ marginLeft: '18px' }}>
          <span className="text-xs font-medium text-gray-500">
            {deals.length} deal{deals.length !== 1 ? 's' : ''}
          </span>
          <span className="text-sm font-semibold text-gray-500">
            ${totalValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </span>
        </div>
      </div>

      {/* Cards - scrollable */}
      <div className="p-3 overflow-y-auto flex-1 thin-scrollbar">
        <SortableContext items={deals.map(d => d.id)} strategy={verticalListSortingStrategy}>
          {deals.length > 0 ? (
            deals.map((deal) => (
              <DealCard key={deal.id} deal={deal} stage={stage} />
            ))
          ) : (
            <p className="text-gray-400 text-sm mt-2">No deals</p>
          )}
        </SortableContext>
      </div>
    </div>
  );
};

const getWeekRange = (offset = 0) => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const startOfCurrentWeek = new Date(today);
  startOfCurrentWeek.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  startOfCurrentWeek.setHours(0, 0, 0, 0);
  const start = new Date(startOfCurrentWeek);
  start.setDate(startOfCurrentWeek.getDate() + offset * 7);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const getMonthRange = (offset = 0) => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const end = new Date(today.getFullYear(), today.getMonth() + offset + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const formatWeekLabel = (offset) => {
  const { start, end } = getWeekRange(offset);
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const suffix = offset === 0 ? ' (This Week)' : offset === -1 ? ' (Last Week)' : '';
  return `${fmt(start)} – ${fmt(end)}${suffix}`;
};

const formatMonthLabel = (offset) => {
  const { start } = getMonthRange(offset);
  const label = start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const suffix = offset === 0 ? ' (This Month)' : offset === -1 ? ' (Last Month)' : '';
  return `${label}${suffix}`;
};

const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
};

const getWeeksInMonth = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const weeks = [];
  let current = getWeekStart(firstDay);
  while (current <= lastDay) {
    const end = new Date(current);
    end.setDate(current.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    weeks.push({ start: new Date(current), end });
    current = new Date(current);
    current.setDate(current.getDate() + 7);
  }
  return weeks;
};

const getWeekOffset = (targetWeekStart) => {
  const currentWeekStart = getWeekStart(new Date());
  return Math.round((targetWeekStart - currentWeekStart) / (7 * 24 * 60 * 60 * 1000));
};

const getMonthOffset = (year, month) => {
  const today = new Date();
  return (year - today.getFullYear()) * 12 + (month - today.getMonth());
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PeriodPicker = ({ period, periodOffset, onSelect, onClose }) => {
  const today = new Date();

  const initYear = () => {
    const { start } = period === 'monthly' ? getMonthRange(periodOffset) : getWeekRange(periodOffset);
    return start.getFullYear();
  };

  const [year, setYear] = useState(initYear);
  const [monthForWeeks, setMonthForWeeks] = useState(null);

  const isFutureMonth = (y, m) =>
    y > today.getFullYear() || (y === today.getFullYear() && m > today.getMonth());

  const containerStyle = {
    position: 'absolute', top: '100%', right: 0, marginTop: 6,
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: 16, boxShadow: '0 8px 24px rgba(0,0,0,0.13)', zIndex: 100, minWidth: 244,
  };

  const navBtnStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, borderRadius: 6, border: '1px solid #e2e8f0',
    background: '#f8fafc', cursor: 'pointer', color: '#475569', flexShrink: 0,
  };

  if (period === 'weekly' && monthForWeeks !== null) {
    const weeks = getWeeksInMonth(year, monthForWeeks);
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const currentWeekStart = getWeekStart(new Date());

    return (
      <div style={containerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, gap: 6 }}>
          <button style={navBtnStyle} onClick={() => setMonthForWeeks(null)}>
            <ChevronLeft size={13} />
          </button>
          <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>
            {MONTH_NAMES[monthForWeeks]} {year}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {weeks.map((week, i) => {
            const isFuture = week.start > currentWeekStart;
            const isActive = getWeekOffset(week.start) === periodOffset;
            return (
              <button
                key={i}
                disabled={isFuture}
                onClick={() => { if (!isFuture) { onSelect(getWeekOffset(week.start)); onClose(); } }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', borderRadius: 7, border: 'none',
                  background: isActive ? '#3b82f6' : isFuture ? 'transparent' : '#f8fafc',
                  color: isActive ? '#fff' : isFuture ? '#cbd5e1' : '#334155',
                  cursor: isFuture ? 'not-allowed' : 'pointer',
                  fontSize: '0.8rem', fontWeight: isActive ? 700 : 500,
                }}
                onMouseEnter={e => { if (!isFuture && !isActive) e.currentTarget.style.background = '#eff6ff'; }}
                onMouseLeave={e => { if (!isFuture && !isActive) e.currentTarget.style.background = '#f8fafc'; }}
              >
                <span style={{ color: isActive ? '#bfdbfe' : '#94a3b8', fontSize: '0.72rem', minWidth: 22 }}>W{i + 1}</span>
                <span>{fmt(week.start)} – {fmt(week.end)}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <button style={navBtnStyle} onClick={() => setYear(y => y - 1)}>
          <ChevronLeft size={13} />
        </button>
        <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>
          {year}
        </span>
        <button
          style={{ ...navBtnStyle, opacity: year >= today.getFullYear() ? 0.35 : 1, cursor: year >= today.getFullYear() ? 'not-allowed' : 'pointer' }}
          disabled={year >= today.getFullYear()}
          onClick={() => setYear(y => y + 1)}
        >
          <ChevronRight size={13} />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
        {MONTH_NAMES.map((name, i) => {
          const future = isFutureMonth(year, i);
          const isActive = period === 'monthly' && getMonthOffset(year, i) === periodOffset;
          return (
            <button
              key={i}
              disabled={future}
              onClick={() => {
                if (future) return;
                if (period === 'monthly') { onSelect(getMonthOffset(year, i)); onClose(); }
                else setMonthForWeeks(i);
              }}
              style={{
                padding: '8px 4px', borderRadius: 7, border: 'none',
                background: isActive ? '#3b82f6' : future ? 'transparent' : '#f8fafc',
                color: isActive ? '#fff' : future ? '#cbd5e1' : '#334155',
                cursor: future ? 'not-allowed' : 'pointer',
                fontSize: '0.82rem', fontWeight: isActive ? 700 : 500,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (!future && !isActive) e.currentTarget.style.background = '#eff6ff'; }}
              onMouseLeave={e => { if (!future && !isActive) e.currentTarget.style.background = '#f8fafc'; }}
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const filterDealsByPeriod = (deals, period, offset = 0) => {
  if (period === "all") return deals;
  if (period === "weekly") {
    const { start, end } = getWeekRange(offset);
    return deals.filter((d) => {
      const created = new Date(d.created_at);
      return created >= start && created <= end;
    });
  }
  // monthly
  const { start, end } = getMonthRange(offset);
  return deals.filter((d) => {
    const created = new Date(d.created_at);
    return created >= start && created <= end;
  });
};

const filterDealsByAuthor = (deals, author) => {
  if (author === "all") return deals;
  return deals.filter((d) => d.created_by_name === author);
};

const filterDealsByCategory = (deals, category) => {
  return deals.filter((d) => (d.category || "regular") === category);
};

// Persist filter state across navigation (e.g. Pipeline -> Deal Details ->
// Back) so returning to this page keeps the previous view. Scoped to the
// tab's session, same pattern as DealsPage.js.
const PIPELINE_FILTERS_STORAGE_KEY = "pipelinePage:filters";

const getPersistedPipelineFilters = () => {
  try {
    const raw = window.sessionStorage.getItem(PIPELINE_FILTERS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const PipelinePage = () => {
  const [stages, setStages] = useState([]);
  const [allDeals, setAllDeals] = useState([]);
  const [columns, setColumns] = useState({});
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(() => getPersistedPipelineFilters()?.period ?? "all");
  const [periodOffset, setPeriodOffset] = useState(() => getPersistedPipelineFilters()?.periodOffset ?? 0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [authorFilter, setAuthorFilter] = useState(() => getPersistedPipelineFilters()?.authorFilter ?? "all");
  const [categoryFilter, setCategoryFilter] = useState(() => getPersistedPipelineFilters()?.categoryFilter ?? "regular");
  const pickerRef = useRef(null);
  const { user } = useAuth();
  const toast = useToast();
  const canViewAuthorFilter = user?.role === 'admin' || user?.role === 'executive';

  const buildColumns = (stagesList, deals) => {
    const grouped = {};
    stagesList.forEach((s) => (grouped[s.key] = []));
    deals.forEach((deal) => {
      let stageKey = '';
      if (typeof deal.stage === 'object' && deal.stage !== null && deal.stage.key) {
        stageKey = deal.stage.key;
      } else if (typeof deal.stage === 'string') {
        stageKey = deal.stage.toLowerCase();
      }
      if (stageKey && grouped.hasOwnProperty(stageKey)) {
        grouped[stageKey].push(deal);
      }
    });
    return grouped;
  };

  const getUniqueAuthors = () => {
    const uniqueNames = new Set();
    allDeals.forEach(deal => {
      if (deal.created_by_name) {
        uniqueNames.add(deal.created_by_name);
      }
    });
    return Array.from(uniqueNames).sort();
  };

  const fetchPipeline = async () => {
    setLoading(true);
    try {
      const [stagesRes, dealsRes] = await Promise.all([
        api.get('/api/stages/'),
        api.get('/api/deals/', { params: { limit: 5000 } }),
      ]);
      const stagesList = stagesRes.data || [];
      const dealsList = dealsRes.data || [];
      setStages(stagesList);
      setAllDeals(dealsList);
      const filteredByPeriod = filterDealsByPeriod(dealsList, period, periodOffset);
      const filteredByAuthor = filterDealsByAuthor(filteredByPeriod, authorFilter);
      const filteredByCategory = filterDealsByCategory(filteredByAuthor, categoryFilter);
      setColumns(buildColumns(stagesList, filteredByCategory));
    } catch (e) {
      console.error('Pipeline fetch error:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (stages.length > 0) {
      const filteredByPeriod = filterDealsByPeriod(allDeals, period, periodOffset);
      const filteredByAuthor = filterDealsByAuthor(filteredByPeriod, authorFilter);
      const filteredByCategory = filterDealsByCategory(filteredByAuthor, categoryFilter);
      setColumns(buildColumns(stages, filteredByCategory));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, periodOffset, authorFilter, categoryFilter]);

  useEffect(() => {
    fetchPipeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist filter state so navigating to a deal (and back) keeps the
  // previous filtered view instead of resetting to defaults.
  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        PIPELINE_FILTERS_STORAGE_KEY,
        JSON.stringify({ period, periodOffset, authorFilter, categoryFilter })
      );
    } catch {
      // Ignore storage errors (e.g. private browsing quota exceeded)
    }
  }, [period, periodOffset, authorFilter, categoryFilter]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    if (pickerOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pickerOpen]);

  const updateDealStage = async (dealId, newStage) => {
    try {
      await api.put(`/api/deals/${dealId}`, { stage: newStage });
    } catch (err) {
      console.error("Stage update failed:", err);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) return;

    const dealId = active.id;
    
    // Find source stage
    let sourceStage = null;
    for (const [stageKey, deals] of Object.entries(columns)) {
      if (deals.find(d => d.id === dealId)) {
        sourceStage = stageKey;
        break;
      }
    }

    if (!sourceStage) return;

    // Determine destination stage
    let destinationStage = null;
    
    // Check if over.id is a stage key
    if (columns.hasOwnProperty(over.id)) {
      destinationStage = over.id;
    } else {
      // It's a deal ID, find which stage it belongs to
      for (const [stageKey, deals] of Object.entries(columns)) {
        if (deals.find(d => d.id === over.id)) {
          destinationStage = stageKey;
          break;
        }
      }
    }

    if (!destinationStage || sourceStage === destinationStage) return;

    // Get the deal and create new state
    const deal = columns[sourceStage].find(d => d.id === dealId);
    if (!deal) return;

    const newColumns = { ...columns };
    
    // Ensure destination stage exists in columns
    if (!newColumns[destinationStage]) {
      console.error('Invalid destination stage:', destinationStage);
      return;
    }
    
    const updatedDeal = { ...deal, stage: destinationStage };
    newColumns[sourceStage] = newColumns[sourceStage].filter(d => d.id !== dealId);
    newColumns[destinationStage] = [...newColumns[destinationStage], updatedDeal];

    setColumns(newColumns);
    setAllDeals(prev => prev.map(d => d.id === dealId ? updatedDeal : d));

    // Update backend
    updateDealStage(dealId, destinationStage).catch((err) => {
      console.error("Failed to update deal stage:", err);
      // Show error feedback to user
      setColumns(columns);
      toast.error(`Failed to move deal. Error: ${err.message || 'Unknown error'}. Please try again.`);
      fetchPipeline();
    });
  };

  if (loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        gap: "0.5rem",
      }}>
        <Loader2 size={48} color="#3b82f6" style={{ animation: "spin 1s linear infinite" }} />
        <div style={{ fontSize: "1.05rem", color: "#64748b", fontWeight: 500 }}>Loading pipeline...</div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const PERIODS = [
    { key: "all", label: "All" },
    { key: "weekly", label: "Weekly" },
    { key: "monthly", label: "Monthly" },
  ];

  return (
    <div className="p-6 h-full flex flex-col bg-gray-50">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <UiText variant="title" as="h1" className="mb-1">Sales Pipeline</UiText>
          <UiText variant="subtitle">Drag deals between stages to update their status</UiText>
          <div style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
            Stages: {stages.length} | Deals shown: {Object.values(columns).reduce((sum, arr) => sum + arr.length, 0)}
          </div>
        </div>

        {/* Period + Author controls */}
        <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end", gap: "8px", alignSelf: "flex-start" }}>
          {/* Category filter - Regular vs Federal */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              padding: "0.4rem 1rem",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              fontWeight: 500,
              fontSize: "0.9rem",
              cursor: "pointer",
              background: "#fff",
              color: "#334155",
              minWidth: "140px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}
            title="Filter by Category"
          >
            <option value="regular">Regular</option>
            <option value="federal">Federal</option>
          </select>

          {/* Author filter - admin and executive only */}
          {canViewAuthorFilter && (
            <select
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}
              style={{
                padding: "0.4rem 1rem",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                fontWeight: 500,
                fontSize: "0.9rem",
                cursor: "pointer",
                background: "#fff",
                color: "#334155",
                minWidth: "140px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
              title="Filter by Author"
            >
              <option value="all">All Authors</option>
              {getUniqueAuthors().map((author) => (
                <option key={author} value={author}>{author}</option>
              ))}
            </select>
          )}

          {/* Period toggle */}
          <div style={{ display: "flex", background: "#f1f5f9", borderRadius: "8px", padding: "3px" }}>
            {PERIODS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => { setPeriod(key); setPeriodOffset(0); }}
                style={{
                  padding: "0.4rem 1rem",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  background: period === key ? "#3b82f6" : "transparent",
                  color: period === key ? "#fff" : "#64748b",
                  transition: "all 0.2s ease",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Week / Month navigator */}
          {period !== "all" && (
            <div ref={pickerRef} style={{ position: 'relative' }}>
              <div style={{
                display: "flex", alignItems: "center", gap: "6px",
                background: "#fff", border: "1px solid #e2e8f0",
                borderRadius: "8px", padding: "4px 8px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}>
                <button
                  onClick={() => setPeriodOffset(o => o - 1)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 28, height: 28, borderRadius: "6px",
                    border: "1px solid #e2e8f0", background: "#f8fafc",
                    cursor: "pointer", color: "#475569",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#e2e8f0"}
                  onMouseLeave={e => e.currentTarget.style.background = "#f8fafc"}
                >
                  <ChevronLeft size={14} />
                </button>

                <button
                  onClick={() => setPickerOpen(o => !o)}
                  style={{
                    fontSize: "0.8rem", fontWeight: 600, color: "#334155",
                    minWidth: 160, textAlign: "center", background: pickerOpen ? "#eff6ff" : "transparent",
                    border: "1px solid " + (pickerOpen ? "#bfdbfe" : "transparent"),
                    borderRadius: 6, padding: "3px 8px", cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {period === "weekly" ? formatWeekLabel(periodOffset) : formatMonthLabel(periodOffset)}
                </button>

                <button
                  onClick={() => setPeriodOffset(o => Math.min(o + 1, 0))}
                  disabled={periodOffset >= 0}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 28, height: 28, borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    background: periodOffset >= 0 ? "#f1f5f9" : "#f8fafc",
                    cursor: periodOffset >= 0 ? "not-allowed" : "pointer",
                    color: periodOffset >= 0 ? "#cbd5e1" : "#475569",
                  }}
                  onMouseEnter={e => { if (periodOffset < 0) e.currentTarget.style.background = "#e2e8f0"; }}
                  onMouseLeave={e => { if (periodOffset < 0) e.currentTarget.style.background = "#f8fafc"; }}
                >
                  <ChevronRight size={14} />
                </button>
              </div>

              {pickerOpen && (
                <PeriodPicker
                  period={period}
                  periodOffset={periodOffset}
                  onSelect={(offset) => setPeriodOffset(offset)}
                  onClose={() => setPickerOpen(false)}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <DndContext
        collisionDetection={pointerWithin}
        onDragEnd={handleDragEnd}
      >
        <div
          className="thin-scrollbar"
          style={{ display: 'flex', gap: '1rem', padding: '0.5rem', width: '100%', overflowX: 'auto' }}
        >
          {stages.length === 0 ? (
            <div className="text-gray-400">No stages available</div>
          ) : (
            stages.map((stage) => (
              <StageColumn key={stage.key} stage={stage} deals={columns[stage.key] || []} />
            ))
          )}
        </div>
      </DndContext>
    </div>
  );
};

export default PipelinePage;
